import { cn } from '@/lib/cn';
import type { AssociateAssignmentStatus, TransactionStatus } from '@shared/types/api';

const LABEL: Record<TransactionStatus, string> = {
  SERVICE_REQUESTED: 'Service Requested',
  CHAT_CREATED: 'Chat Created',
  ASSOCIATE_ASSIGNMENT_PENDING: 'Assignment Pending',
  ASSOCIATE_ASSIGNED: 'Associate Assigned',
  ASSOCIATE_ACCEPTANCE_PENDING: 'Acceptance Pending',
  ASSOCIATE_ACCEPTED: 'Associate Accepted',
  QUOTATION_REVIEW_PENDING: 'Quotation Review Pending',
  CLIENT_CONFIRMATION_PENDING: 'Client Confirmation Pending',
  CLIENT_CONFIRMED: 'Associate Verified',
  PAYMENT_PENDING: 'Payment Pending',
  INITIATED: 'Chat Created',
  INVOICE_CREATED: 'Invoice Sent',
  PAID: 'Paid',
  PAYMENT_RECEIVED: 'Payment Received',
  ASSIGNED: 'Associate Assigned',
  ASSIGNED_REVOKED: 'Reassigning',
  IN_PROGRESS: 'In Progress',
  DRAFT_PREPARATION: 'Draft Preparation',
  CLIENT_DRAFT_CONFIRMATION: 'Draft Under Review',
  REVIEW: 'In Review',
  REVISION_REQUESTED: 'Revision Requested',
  SENT_FOR_APPROVAL: 'Sent For Approval',
  APPROVED: 'Approved',
  FINAL_RESULT_RECEIVED: 'Final Result Ready',
  DELIVERED: 'Delivered',
  FINAL_DOC_READY: 'Final Doc Ready',
  GRIEVANCE_RAISED: 'Grievance / Correction',
  CLIENT_CONFIRMED_FINAL: 'Client Confirmed',
  RATING_PENDING: 'Rating Pending',
  SETTLEMENT_PENDING: 'Settlement Pending',
  SETTLED: 'Payout Settled',
  COMPLETE: 'Completed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const TONE: Record<TransactionStatus, string> = {
  SERVICE_REQUESTED: 'bg-gray-100 text-gray-700',
  CHAT_CREATED: 'bg-gray-100 text-gray-700',
  ASSOCIATE_ASSIGNMENT_PENDING: 'bg-amber-50 text-amber-800',
  ASSOCIATE_ASSIGNED: 'bg-blue-50 text-blue-800',
  ASSOCIATE_ACCEPTANCE_PENDING: 'bg-amber-50 text-amber-800',
  ASSOCIATE_ACCEPTED: 'bg-blue-50 text-blue-800',
  QUOTATION_REVIEW_PENDING: 'bg-amber-50 text-amber-800',
  CLIENT_CONFIRMATION_PENDING: 'bg-brand-50 text-brand-800',
  CLIENT_CONFIRMED: 'bg-brand-50 text-brand-800',
  PAYMENT_PENDING: 'bg-amber-50 text-amber-800',
  INITIATED: 'bg-gray-100 text-gray-700',
  INVOICE_CREATED: 'bg-amber-50 text-amber-800',
  PAID: 'bg-blue-50 text-blue-800',
  PAYMENT_RECEIVED: 'bg-blue-50 text-blue-800',
  ASSIGNED: 'bg-blue-50 text-blue-800',
  ASSIGNED_REVOKED: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-brand-50 text-brand-800',
  DRAFT_PREPARATION: 'bg-purple-50 text-purple-800',
  CLIENT_DRAFT_CONFIRMATION: 'bg-purple-50 text-purple-800',
  REVIEW: 'bg-purple-50 text-purple-800',
  REVISION_REQUESTED: 'bg-orange-50 text-orange-800',
  SENT_FOR_APPROVAL: 'bg-indigo-50 text-indigo-800',
  APPROVED: 'bg-brand-50 text-brand-800',
  FINAL_RESULT_RECEIVED: 'bg-emerald-50 text-emerald-800',
  DELIVERED: 'bg-emerald-50 text-emerald-800',
  FINAL_DOC_READY: 'bg-emerald-50 text-emerald-800',
  GRIEVANCE_RAISED: 'bg-red-50 text-red-700',
  CLIENT_CONFIRMED_FINAL: 'bg-brand-50 text-brand-800',
  RATING_PENDING: 'bg-amber-50 text-amber-800',
  SETTLEMENT_PENDING: 'bg-indigo-50 text-indigo-800',
  SETTLED: 'bg-emerald-50 text-emerald-800',
  COMPLETE: 'bg-gray-100 text-gray-700',
  CLOSED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-50 text-red-700',
};

export function StatusBadge({
  status,
  assignmentStatus,
  isExpired,
  className,
}: {
  status: TransactionStatus;
  assignmentStatus?: AssociateAssignmentStatus | string | null;
  isExpired?: boolean;
  className?: string;
}) {
  if (assignmentStatus === 'DECLINED') {
    return (
      <span
        className={cn(
          'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-800 border border-red-200',
          className,
        )}
      >
        Declined
      </span>
    );
  }

  if (assignmentStatus === 'EXPIRED' || isExpired) {
    return (
      <span
        className={cn(
          'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200',
          className,
        )}
      >
        Expired
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        TONE[status] ?? 'bg-gray-100 text-gray-700',
        className,
      )}
    >
      {LABEL[status] ?? status}
    </span>
  );
}
